import { useState } from 'react';
import { ChevronRight, ChevronDown, Loader2 } from 'lucide-react';
import type { ToolStepBlock } from '@netsuite-ai/types';

const STATUS_NOTE: Partial<Record<ToolStepBlock['status'], string>> = {
  awaiting_approval: 'awaiting approval',
  skipped: 'skipped',
  cancelled: 'stopped',
};

function seconds(ms: number) {
  return ms < 1000 ? '<1s' : `${Math.round(ms / 1000)}s`;
}

/** One call inside the activity card; expands to the SuiteQL it ran. */
function StepRow({ step }: { step: ToolStepBlock }) {
  const [open, setOpen] = useState(false);
  const note = STATUS_NOTE[step.status];
  const meta = [
    step.rows !== undefined && `${step.rows} row${step.rows === 1 ? '' : 's'}`,
    note,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <li>
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={!step.query}
        aria-expanded={step.query ? open : undefined}
        className="text-ink/80 flex w-full items-center gap-1.5 text-left disabled:cursor-default"
      >
        {step.status === 'running' && (
          <Loader2 size={12} className="text-ink/40 shrink-0 motion-safe:animate-spin" />
        )}
        <span>{step.label}</span>
        {meta && <span className="text-ink/40 text-xs">· {meta}</span>}
        {step.query &&
          (open ? (
            <ChevronDown size={12} className="text-ink/40 shrink-0" />
          ) : (
            <ChevronRight size={12} className="text-ink/40 shrink-0" />
          ))}
      </button>
      {open && step.query && (
        <pre className="bg-ink/5 text-ink/80 mt-1.5 overflow-x-auto rounded-md px-3 py-2 font-mono text-xs whitespace-pre-wrap">
          {step.query}
        </pre>
      )}
    </li>
  );
}

/**
 * A run of consecutive tool calls, collapsed to the latest step's label and
 * expanding into a card of every step — the same shape as a chat "thinking" panel.
 */
export function ToolActivity({ steps: all }: { steps: ToolStepBlock[] }) {
  const [open, setOpen] = useState(false);
  // A failed query is the model's scratch work — it retries with a corrected one.
  // The step stays in the stored message; it just isn't shown.
  const steps = all.filter((s) => s.status !== 'error');
  const running = steps.find((s) => s.status === 'running');
  if (steps.length === 0) return null;
  const headline = (running ?? steps[steps.length - 1]).label;
  const totalMs = steps.reduce((sum, s) => sum + (s.ms ?? 0), 0);

  return (
    <div className="my-2">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="text-ink/60 hover:text-ink/80 flex items-center gap-1.5 text-sm"
      >
        {running && <Loader2 size={14} className="shrink-0 motion-safe:animate-spin" />}
        <span>{headline}</span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>

      {open && (
        <div className="border-ink/10 mt-2 rounded-xl border px-3 py-2.5 text-sm">
          <p className="text-ink/50 mb-1.5 text-xs">
            {running
              ? 'Working…'
              : `Worked for ${seconds(totalMs)} · ${steps.length} step${steps.length === 1 ? '' : 's'}`}
          </p>
          <ol className="space-y-1.5">
            {steps.map((s) => (
              <StepRow key={s.id} step={s} />
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
